import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { EditorState, ContentState, convertFromHTML } from 'draft-js';
import { convertHTMlToRaw } from '../../../utils/HTMLDraft';
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../../utils/sentryHelpers';
import { uploadPublicDocument, validatePublicDocument } from '../../../utils/uploadPublicDocument';
import { slugMutationInput } from '../../../utils/slugInput';

const client = generateClient();

export function useProductForm({ companyId, productId, currentIndustry, isAdminUser, canEditSlug }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEditMode = !!productId;

  // Core product data
  const [productData, setProductData] = useState({
    name: [],
    content: [],
    images: {},
    file: {},
    file3DModel: {},
    cMSProductEmployeeId: "",
    cMSProductTopicId: "",
    conditions: [],
  });

  // Loading & error
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Slug as loaded with the product; "slug" is only sent when it was changed here (see slugMutationInput)
  const [loadedSlug, setLoadedSlug] = useState(null);

  // Validation
  const [touchedFields, setTouchedFields] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Topics
  const [topics, setTopics] = useState([]);
  const [topicGroups, setTopicGroups] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectTopicsModal, setSelectTopicsModal] = useState(false);
  const [unknownTopicNames, setUnknownTopicNames] = useState({});

  // Employee
  const [employee, setEmployee] = useState(null);
  const [selectEmployeeModal, setSelectEmployeeModal] = useState(false);

  // Editor (Draft.js)
  const [editorState, setEditorState] = useState({
    de: EditorState.createEmpty(),
    en: EditorState.createEmpty(),
  });

  // Image upload
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadingImageError, setUploadingImageError] = useState(null);

  // File upload
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadingFileError, setUploadingFileError] = useState(null);

  // 3D Model (deferred upload)
  const [pending3DModelFile, setPending3DModelFile] = useState(null);
  const [preview3DModelUrl, setPreview3DModelUrl] = useState(null);
  const [upload3DModelError, setUpload3DModelError] = useState(null);


  // ─── Validation ───────────────────────────────────────────────

  const SECTION_FIELDS = {
    1: ['cMSProductTopicId'],
    2: ['name_de', 'name_en', 'content_de', 'content_en'],
    3: ['image', 'imageCondition', 'file3DModelCondition', 'fileCondition'],
    4: ['cMSProductEmployeeId'],
  };

  const validateField = useCallback((fieldName) => {
    let error = null;
    switch (fieldName) {
      case 'cMSProductTopicId':
        if (!productData.cMSProductTopicId) error = t("Please select a topic");
        break;
      case 'name_de':
        if (!productData.name?.find(n => n.language === "de")?.text) error = t("Please enter a German title");
        break;
      case 'name_en':
        if (!productData.name?.find(n => n.language === "en")?.text) error = t("Please enter an English title");
        break;
      case 'content_de':
        if (!productData.content?.find(c => c.language === "de")?.text) error = t("Please enter German content");
        break;
      case 'content_en':
        if (!productData.content?.find(c => c.language === "en")?.text) error = t("Please enter English content");
        break;
      case 'image':
        if (!productData.images?.main?.fileName) error = t("Please upload an image");
        break;
      case 'imageCondition':
        if (productData.images?.main?.fileName && !productData.conditions?.find(c => c.name === "image")?.accepted)
          error = t("Please agree to the image conditions");
        break;
      case 'file3DModelCondition':
        if ((productData.file3DModel?.fileName || pending3DModelFile) && !productData.conditions?.find(c => c.name === "file3DModel")?.accepted)
          error = t("Please agree to the 3D model conditions");
        break;
      case 'fileCondition':
        if (productData.file?.fileName && !productData.conditions?.find(c => c.name === "file")?.accepted)
          error = t("Please agree to the file conditions");
        break;
      case 'cMSProductEmployeeId':
        if (!productData.cMSProductEmployeeId) error = t("Please select an employee");
        break;
    }
    setValidationErrors(prev => ({ ...prev, [fieldName]: error }));
    return error;
  }, [productData, pending3DModelFile, t]);

  const markFieldTouched = useCallback((fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    validateField(fieldName);
  }, [validateField]);

  const validateSection = useCallback((sectionNumber) => {
    const fields = SECTION_FIELDS[sectionNumber] || [];
    const newTouched = {};
    const errors = {};
    let hasErrors = false;

    for (const field of fields) {
      newTouched[field] = true;
      let error = null;
      switch (field) {
        case 'cMSProductTopicId':
          if (!productData.cMSProductTopicId) error = t("Please select a topic");
          break;
        case 'name_de':
          if (!productData.name?.find(n => n.language === "de")?.text) error = t("Please enter a German title");
          break;
        case 'name_en':
          if (!productData.name?.find(n => n.language === "en")?.text) error = t("Please enter an English title");
          break;
        case 'content_de':
          if (!productData.content?.find(c => c.language === "de")?.text) error = t("Please enter German content");
          break;
        case 'content_en':
          if (!productData.content?.find(c => c.language === "en")?.text) error = t("Please enter English content");
          break;
        case 'image':
          if (!productData.images?.main?.fileName) error = t("Please upload an image");
          break;
        case 'imageCondition':
          if (productData.images?.main?.fileName && !productData.conditions?.find(c => c.name === "image")?.accepted)
            error = t("Please agree to the image conditions");
          break;
        case 'file3DModelCondition':
          if ((productData.file3DModel?.fileName || pending3DModelFile) && !productData.conditions?.find(c => c.name === "file3DModel")?.accepted)
            error = t("Please agree to the 3D model conditions");
          break;
        case 'fileCondition':
          if (productData.file?.fileName && !productData.conditions?.find(c => c.name === "file")?.accepted)
            error = t("Please agree to the file conditions");
          break;
        case 'cMSProductEmployeeId':
          if (!productData.cMSProductEmployeeId) error = t("Please select an employee");
          break;
      }
      errors[field] = error;
      if (error) hasErrors = true;
    }

    setTouchedFields(prev => ({ ...prev, ...newTouched }));
    setValidationErrors(prev => ({ ...prev, ...errors }));
    return !hasErrors;
  }, [productData, t]);

  const validateAll = useCallback(() => {
    let allValid = true;
    for (let section = 1; section <= 4; section++) {
      if (!validateSection(section)) allValid = false;
    }
    return allValid;
  }, [validateSection]);

  // Returns the first invalid section number (1-4) or null if all valid
  const validateAllAndGetFirstInvalid = useCallback(() => {
    setSubmitAttempted(true);
    let firstInvalid = null;
    for (let section = 1; section <= 4; section++) {
      if (!validateSection(section) && firstInvalid === null) {
        firstInvalid = section;
      }
    }
    return firstInvalid;
  }, [validateSection]);

  // Pure data check: is section complete? (no touching needed)
  const isSectionComplete = useCallback((sectionNumber) => {
    switch (sectionNumber) {
      case 1:
        return !!productData.cMSProductTopicId;
      case 2:
        return !!(
          productData.name?.find(n => n.language === "de")?.text &&
          productData.name?.find(n => n.language === "en")?.text &&
          productData.content?.find(c => c.language === "de")?.text &&
          productData.content?.find(c => c.language === "en")?.text
        );
      case 3: {
        const hasImage = !!productData.images?.main?.fileName;
        const imageCondOk = !hasImage || !!productData.conditions?.find(c => c.name === "image")?.accepted;
        const has3DModel = !!(productData.file3DModel?.fileName || pending3DModelFile);
        const file3DModelCondOk = !has3DModel || !!productData.conditions?.find(c => c.name === "file3DModel")?.accepted;
        const fileCondOk = !productData.file?.fileName || !!productData.conditions?.find(c => c.name === "file")?.accepted;
        return hasImage && imageCondOk && file3DModelCondOk && fileCondOk;
      }
      case 4:
        return !!productData.cMSProductEmployeeId;
      default:
        return false;
    }
  }, [productData]);

  const getFieldError = useCallback((fieldName) => {
    if (!touchedFields[fieldName]) return null;
    return validationErrors[fieldName] || null;
  }, [touchedFields, validationErrors]);

  // ─── GraphQL Operations ───────────────────────────────────────

  const getProductData = async () => {
    try {
      const productData = await client.graphql({
        variables: { id: productId },
        query: /* GraphQL */ `
          query GetCMSProduct($id: ID!) {
            getCMSProduct(id: $id) {
              id
              name {
                language
                text
              }
              slug
              slugHistory
              content {
                language
                text
              }
              images {
                main {
                  fileName
                  alt
                }
              }
              file {
                url
                s3Path
                fileName
                type
                name
              }
              file3DModel {
                url
                s3Path
                fileName
                type
                name
              }
              conditions {
                name
                accepted
              }
              cMSProductEmployeeId
              cMSProductTopicId
            }
          }
        `,
      });

      return {
        ...productData.data.getCMSProduct,
        topics: productData.data.getCMSProduct.topics?.items?.map(topic => topic.topicId) || []
      };
    } catch (error) {
      console.warn("Error getting product data", error);
      captureError(error, {
        feature: 'products',
        operation: 'fetch-product-data',
        context: { productId }
      });
    }
  };

  const intialLoadProduct = async () => {
    setIsLoading(true);
    const dbProduct = await getProductData();
    if (!dbProduct) {
      setIsLoading(false);
      setLoadError(true);
      return;
    }
    setProductData(dbProduct);
    setLoadedSlug(dbProduct.slug ?? null);
    if (dbProduct.content) {
      const deContent = dbProduct.content.find(c => c.language === "de")?.text;
      const enContent = dbProduct.content.find(c => c.language === "en")?.text;
      setEditorState({
        de: deContent ? EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML(deContent)
          )
        ) : EditorState.createEmpty(),
        en: enContent ? EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML(enContent)
          )
        ) : EditorState.createEmpty(),
      });
    }
    setIsLoading(false);
  };

  // Initial product load
  useEffect(() => {
    if (!companyId) {
      console.warn("No company id provided");
      return;
    } else if (!productId) {
      console.log("No product id provided");
      return;
    } else {
      intialLoadProduct();
    }
  }, []);

  // ─── Topics ───────────────────────────────────────────────────

  useEffect(() => {
    if (productData.cMSProductTopicId) {
      setSelectedTopics([productData.cMSProductTopicId]);
    }
  }, [productData.cMSProductTopicId]);

  const toggleSelectTopicsModal = () => setSelectTopicsModal(prev => !prev);

  const handleSetSelectedTopics = (topics) => {
    setSelectedTopics(topics);
    setProductData(oldData => ({ ...oldData, cMSProductTopicId: topics[0] }));
    setValidationErrors(prev => ({ ...prev, cMSProductTopicId: null }));
  };

  const getTopics = async () => {
    const filter = currentIndustry?.id ? { industryId: { eq: currentIndustry.id } } : undefined;
    try {
      const topicGroupsWithTopics = await client.graphql({
        query: /* GraphQL */ `
          query ListTopicGroups(
            $filter: ModelTopicGroupFilterInput
            $limit: Int
            $nextToken: String
          ) {
            listTopicGroups(filter: $filter, limit: $limit, nextToken: $nextToken) {
              items {
                id
                color
                name {
                  language
                  text
                }
                topics {
                  items {
                    id
                    name {
                      language
                      text
                    }
                    topicGroupTopicsId
                  }
                }
              }
              nextToken
              __typename
            }
          }
        `,
        variables: {
          filter,
          limit: 1000
        }
      });
      setTopicGroups(topicGroupsWithTopics.data.listTopicGroups.items);
      const topics = topicGroupsWithTopics.data.listTopicGroups.items.map((topicGroup) => topicGroup.topics.items).flat();
      setTopics(topics);

    } catch (error) {
      console.log("error on fetching topics", error);
      captureError(error, {
        feature: 'products',
        operation: 'fetch-topic-groups'
      });
    }
  };

  useEffect(() => {
    if (currentIndustry?.id) {
      getTopics();
    }
  }, [currentIndustry?.id]);

  useEffect(() => {
    const unknownIds = selectedTopics.filter(id => topics.length > 0 && !topics.find(t => t.id === id));
    if (unknownIds.length === 0) return;
    const fetchUnknownTopics = async () => {
      const names = {};
      for (const id of unknownIds) {
        try {
          const res = await client.graphql({
            query: /* GraphQL */ `query GetTopic($id: ID!) { getTopic(id: $id) { id name { language text } } }`,
            variables: { id }
          });
          if (res.data.getTopic?.name) names[id] = res.data.getTopic.name;
        } catch (e) { /* topic may have been deleted */ }
      }
      setUnknownTopicNames(names);
    };
    fetchUnknownTopics();
  }, [selectedTopics, topics]);

  // ─── Employee ─────────────────────────────────────────────────

  const toggleSelectEmployeeModal = () => setSelectEmployeeModal(prev => !prev);

  const handleSetEmployee = (employees) => {
    setProductData(oldData => ({ ...oldData, cMSProductEmployeeId: employees[0] }));
    setValidationErrors(prev => ({ ...prev, cMSProductEmployeeId: null }));
  };

  const getEmployee = async () => {
    try {
      const employee = await client.graphql({
        query: /* GraphQL */ `
        query GetEmployee($id: ID!) {
            getEmployee(id: $id) {
              id
              user {
                id
                firstName
                lastName
                title
              }
              image {
                fileName
                alt
              }
            }
          }
        `,
        variables: { id: productData.cMSProductEmployeeId },
      });
      setEmployee(employee.data.getEmployee);
    } catch (error) {
      console.log("error on fetching employee", error);
      captureError(error, {
        feature: 'products',
        operation: 'fetch-employee',
        context: { employeeId: productData.cMSProductEmployeeId }
      });
    }
  };

  useEffect(() => {
    if (productData.cMSProductEmployeeId) {
      getEmployee();
    }
  }, [productData.cMSProductEmployeeId]);

  // ─── Editor ───────────────────────────────────────────────────

  const onEditorStateChange = (editorState, language) => {
    setEditorState(oldState => ({ ...oldState, [language]: editorState }));
    setProductData(oldData => ({ ...oldData, content: [...oldData.content.filter(c => c.language !== language), { language, text: convertHTMlToRaw(editorState) }] }));
  };

  // ─── Product Name ─────────────────────────────────────────────

  const setProductName = (language, text) => {
    setProductData(oldData => ({
      ...oldData,
      name: [{ language, text }, ...oldData.name.filter(n => n.language !== language)]
    }));
  };

  // Public URL slug (Admin/Moderator only, see SlugField)
  const setProductSlug = (slug) => {
    setProductData(oldData => ({ ...oldData, slug }));
  };

  // ─── Conditions ───────────────────────────────────────────────

  const handleConditionChange = (conditionName, accepted) => {
    setProductData(oldData => ({
      ...oldData,
      conditions: [{ name: conditionName, accepted }, ...oldData.conditions?.filter(c => c.name !== conditionName)]
    }));
  };

  // ─── Image Upload ─────────────────────────────────────────────

  const handleSetImage = (file) => {
    if (file) {
      try {
        setIsUploadingImage(true);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onerror = error => {
          console.log('Error reader: ', error);
          setUploadingImageError(error);
        }
        reader.onload = async () => {
          try {
            const base64FileBuffer = reader.result.replace(/^data:.+;base64,/, '');
            const response = await client.graphql({
              query: /* GraphQL */ `
          mutation UploadCompanyImage($imageBuffer: String, $imageType: String) {
            uploadCompanyImage(imageBuffer: $imageBuffer, imageType: $imageType)
          }
        `,
              variables: {
                imageBuffer: base64FileBuffer,
                imageType: "productImage",
              },
            });
            const fileName = response.data.uploadCompanyImage.replace(/{|}|fileName=/g, '');
            setProductData(oldData => ({ ...oldData, images: { main: { fileName, alt: "Lecture Image" } } }));

          } catch (error) {
            console.log("error", error);
            captureError(error, {
              feature: 'products',
              operation: 'upload-product-image',
              context: { companyId }
            });
            setIsUploadingImage(false);
            setUploadingImageError(error.errors[0].message);
          }
        }
        setIsUploadingImage(false);
        setUploadingImageError(null);
      } catch (error) {
        console.log("error", error);
        captureError(error, {
          feature: 'products',
          operation: 'upload-product-image-read',
          context: { companyId }
        });
        setIsUploadingImage(false);
        setUploadingImageError(error.errors[0].message);
      }
    }
  };

  // ─── File Upload ──────────────────────────────────────────────

  const handleSetFile = async (file) => {
    if (!file) return;
    const validationError = validatePublicDocument(file, t);
    if (validationError) {
      setUploadingFileError(validationError);
      return;
    }

    setIsUploadingFile(true);
    setUploadingFileError(null);
    try {
      const uploadedFile = await uploadPublicDocument(file, { companyId, fileType: "productFile" });
      setProductData(oldData => ({ ...oldData, file: uploadedFile }));
    } catch (error) {
      captureError(error, {
        feature: 'products',
        operation: 'upload-product-file',
        context: { companyId, fileName: file.name, fileSize: file.size }
      });
      setUploadingFileError(error?.errors?.[0]?.message || t("Upload failed. Please try again."));
    } finally {
      setIsUploadingFile(false);
    }
  };

  // ─── 3D Model ─────────────────────────────────────────────────

  const handleSet3DModel = (file) => {
    if (preview3DModelUrl) URL.revokeObjectURL(preview3DModelUrl);
    setPending3DModelFile(file);
    setPreview3DModelUrl(URL.createObjectURL(file));
  };

  const handleRemove3DModel = () => {
    if (preview3DModelUrl) URL.revokeObjectURL(preview3DModelUrl);
    setPending3DModelFile(null);
    setPreview3DModelUrl(null);
    setProductData(old => ({ ...old, file3DModel: {} }));
  };

  // ─── Submit ───────────────────────────────────────────────────

  const createOrUpdateProduct = async () => {
    try {
      setSubmitAttempted(true);
      if (!validateAll()) {
        return;
      }
      setIsSaving(true);

      // Upload 3D model if pending
      let file3DModelData = productData.file3DModel;
      if (pending3DModelFile) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.replace(/^data:.+;base64,/, ''));
          reader.onerror = reject;
          reader.readAsDataURL(pending3DModelFile);
        });
        const uploadResponse = await client.graphql({
          query: /* GraphQL */ `
            mutation UploadCompanyImage($imageBuffer: String, $imageType: String) {
              uploadCompanyImage(imageBuffer: $imageBuffer, imageType: $imageType)
            }
          `,
          variables: { imageBuffer: base64, imageType: "product3DModel" },
        });
        const raw = uploadResponse.data.uploadCompanyImage;
        if (raw?.includes('error=')) {
          setUpload3DModelError(raw.replace(/{|}|error=/g, ''));
          setIsSaving(false);
          return;
        }
        setUpload3DModelError(null);
        const fileName = raw.replace(/{|}|fileName=/g, '');
        if (fileName) {
          file3DModelData = { fileName, type: "product3DModel" };
        }
      }

      await client.graphql({
        query: productId ? /* GraphQL */ `
        mutation UpdateCMSProduct($input: UpdateCMSProductInput!) {
          updateCMSProduct(input: $input) {
            id
          }
        }
        ` : /* GraphQL */ `
        mutation CreateCMSProduct(
          $input: CreateCMSProductInput!
          $condition: ModelCMSProductConditionInput
        ) {
          createCMSProduct(input: $input, condition: $condition) {
              id
            }
          }
        `,
        variables: {
          input: {
            companyProductsId: companyId,
            name: productData.name,
            content: productData.content,
            images: productData.images,
            file: productData.file,
            ...(file3DModelData?.fileName ? { file3DModel: file3DModelData } : {}),
            cMSProductEmployeeId: productData.cMSProductEmployeeId,
            cMSProductTopicId: productData.cMSProductTopicId,
            conditions: productData.conditions,
            status: "created",
            // "slug" only for Admin/Moderator (AppSync field auth rejects the whole mutation otherwise)
            // and only when it was changed vs. the loaded slug. slugHistory is never sent (input is built explicitly).
            ...slugMutationInput(productData.slug, { canEditSlug, isUpdate: !!productId, loadedSlug }),
            ...productId ? { id: productId } : {},
          },
        },
      });
      setTimeout(() => {
        navigate(`/mycompany/${companyId}/products`);
        setIsSaving(false);
      }, 2000);
    } catch (error) {
      setIsSaving(false);
      console.log("error", error);
      captureError(error, {
        feature: 'products',
        operation: productId ? 'update-product' : 'create-product',
        context: { productId, companyId }
      });
    }
  };

  return {
    // Data
    productData,
    isLoading,
    loadError,
    isSaving,
    isEditMode,

    // Validation
    validationErrors,
    touchedFields,
    markFieldTouched,
    validateField,
    validateSection,
    validateAll,
    validateAllAndGetFirstInvalid,
    getFieldError,
    submitAttempted,
    isSectionComplete,

    // Product data handlers
    setProductName,
    setProductSlug,
    onEditorStateChange,
    editorState,
    handleConditionChange,

    // Topics
    topics,
    topicGroups,
    selectedTopics,
    unknownTopicNames,
    selectTopicsModal,
    setSelectTopicsModal,
    toggleSelectTopicsModal,
    handleSetSelectedTopics,

    // Employee
    employee,
    selectEmployeeModal,
    setSelectEmployeeModal,
    toggleSelectEmployeeModal,
    handleSetEmployee,

    // Image upload
    handleSetImage,
    isUploadingImage,
    uploadingImageError,

    // File upload
    handleSetFile,
    isUploadingFile,
    uploadingFileError,

    // 3D Model
    pending3DModelFile,
    preview3DModelUrl,
    upload3DModelError,
    handleSet3DModel,
    handleRemove3DModel,

    // Submit
    createOrUpdateProduct,
  };
}
